<template>
  <v-dialog :model-value="dialog" @update:model-value="updateDialog" max-width="600px" persistent>
    <v-card>
      <!-- ────── CABECERA ────── -->
      <v-card-title>
        <span class="text-h5">Matricular Curso</span>
      </v-card-title>

      <!-- ────── FORMULARIO ────── -->
      <v-card-text>
        <v-container>
          <v-row>

            <!-- Curso -->
            <v-col cols="12">
              <v-select v-model="selectedCurso" :items="cursos" item-title="descripcion" item-value="id"
                label="Selecciona un curso" :rules="[rules.required]" />

            </v-col>

            <!-- Paquete / precio vigente del curso -->
            <v-col cols="12" v-if="selectedCurso">
              <v-select v-if="paquetes.length" v-model="selectedPaquete" :items="paquetes" :item-title="etiquetaPaquete"
                item-value="id" label="Paquete" :rules="[rules.required]" />
              <v-alert v-else type="warning" variant="tonal" density="compact">
                Este curso no tiene precios vigentes. Configúralo en Catálogos › Paquetes y precios.
              </v-alert>
            </v-col>

            <!-- Medio de contacto (por dónde nos contactaron) -->
            <v-col cols="12">
              <v-select v-model="selectedMedioContacto" :items="mediosContacto" item-title="descripcion"
                item-value="id"
                :label="esPrimeraMatricula ? '¿Por dónde nos contactaron?' : '¿Por dónde nos contactaron? (opcional)'"
                :rules="esPrimeraMatricula ? [rules.required] : []" :hint="esPrimeraMatricula
                  ? 'Obligatorio en la primera matrícula'
                  : 'Opcional: el alumno ya es conocido'" persistent-hint />
            </v-col>

            <!-- Fecha de matrícula -->
            <v-col cols="12">
              <v-text-field v-model="fecha" label="Fecha de Matrícula" type="date" />
            </v-col>

            <!-- Cantidad (solo si se cobra por unidad) -->
            <v-col cols="6" v-if="cantidadEditable">
              <v-text-field v-model.number="cantidad" label="Cantidad (horas / clases)" type="number" min="1"
                @input="calcularTotal" />
            </v-col>

            <!-- Precio del paquete (se cambia en Paquetes y precios; aquí se usa el descuento) -->
            <v-col :cols="cantidadEditable ? 6 : 12">
              <v-text-field :model-value="precio" :label="cantidadEditable ? 'Precio por unidad' : 'Precio del paquete'"
                prefix="S/ " readonly :hint="paqueteSeleccionado?.cantidad ? `Incluye ${paqueteSeleccionado.cantidad} horas / clases` : ''"
                persistent-hint />
            </v-col>

            <!-- Botón descuento -->
            <v-col cols="12">
              <v-btn color="primary" block @click="toggleDescuento">
                {{ mostrarDescuento ? 'Ocultar Descuento' : 'Aplicar Descuento' }}
              </v-btn>
            </v-col>

            <!-- Opciones de descuento -->
            <v-expand-transition>
              <v-row v-show="mostrarDescuento">
                <v-col cols="6">
                  <v-select v-model="tipoDescuento" :items="tiposDescuento" label="Tipo de Descuento" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model.number="descuento" :label="tipoDescuento === 'porcentaje'
                    ? 'Descuento (%)'
                    : 'Descuento (S/)'" type="number" min="0" @input="calcularTotal" />
                </v-col>
              </v-row>
            </v-expand-transition>

            <!-- VISA + comisión -->
            <v-row align="center">
              <v-col cols="6">
                <v-switch v-model="pagoVisa" label="Pago con POS" @change="calcularTotal" />
              </v-col>
              <v-col cols="6" v-if="pagoVisa">
                <v-text-field v-model="comisionIzipay" label="Comisión POS" type="number" prefix="S/ " readonly />
              </v-col>
            </v-row>

            <!-- Total -->
            <v-col cols="12" v-if="descuentoMonto > 0 || comisionIzipay > 0">
              <div class="text-body-2 text-medium-emphasis">
                Subtotal S/ {{ subtotal.toFixed(2) }}
                <span v-if="descuentoMonto > 0"> − descuento S/ {{ descuentoMonto.toFixed(2) }}</span>
                <span v-if="comisionIzipay > 0"> + comisión POS S/ {{ comisionIzipay.toFixed(2) }}</span>
              </div>
            </v-col>
            <v-col cols="12" v-if="error">
              <v-alert type="error" variant="tonal" density="compact">{{ error }}</v-alert>
            </v-col>
            <v-col cols="12">
              <v-alert color="primary" variant="tonal" class="text-center">
                <span class="text-h4">
                  <strong>Precio&nbsp;Final:&nbsp;S/ {{ total }}</strong>
                </span>
              </v-alert>
            </v-col>

          </v-row>
        </v-container>
      </v-card-text>

      <!-- ────── ACCIONES ────── -->
      <v-card-actions>
        <v-spacer />
        <v-btn color="blue-darken-1" text @click="close">Cancelar</v-btn>
        <v-btn color="primary" text
          :disabled="!selectedCurso || !selectedPaquete || !alumnoId || (esPrimeraMatricula && !selectedMedioContacto)"
          :loading="guardando" @click="save">
          Matricular
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'MatricularCursoDialog',

  props: {
    dialog: Boolean,
    alumnoId: {
      type: [Number, String],
      default: null,
    },
    esPrimeraMatricula: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:dialog', 'curso-matriculado'],

  data() {
    return {
      /* selección */
      selectedCurso: null,
      selectedPaquete: null,
      subtotal: 0,
      descuentoMonto: 0,
      guardando: false,
      error: null,
      selectedMedioContacto: null,
      mediosContacto: [],
      cantidad: 1,
      precio: 0,

      /* fecha */
      fecha: new Date().toISOString().slice(0, 10),

      /* descuentos & visa */
      total: 0,
      descuento: 0,
      comisionIzipay: 0,
      pagoVisa: false,
      tipoDescuento: 'porcentaje',
      mostrarDescuento: false,

      /* otras banderas */
      esCuantificable: false,

      /* reglas */
      rules: {
        required: (v) => !!v || 'Este campo es obligatorio',
      },

      tiposDescuento: [
        { title: 'Porcentaje (%)', value: 'porcentaje' },
        { title: 'Monto Fijo (S/)', value: 'monto' },
      ],
    };
  },

  computed: {
    ...mapGetters('curso', ['cursos']),
    // Solo llegan los precios vigentes del curso
    paquetes() {
      return this.cursos.find(c => c.id === this.selectedCurso)?.precio || [];
    },
    paqueteSeleccionado() {
      return this.paquetes.find(p => p.id === this.selectedPaquete) || null;
    },
    cantidadEditable() {
      return this.esCuantificable && !this.paqueteSeleccionado?.cantidad;
    },
  },

  watch: {
    selectedCurso(id) {
      const curso = this.cursos.find(c => c.id === id);
      if (curso) {
        this.esCuantificable = !!Number(curso.cuantificable);
        // Si hay un solo paquete vigente se elige solo
        this.selectedPaquete = this.paquetes.length === 1 ? this.paquetes[0].id : null;
        this.calcularTotal();
      } else {
        this.resetForm();
      }
    },
    pagoVisa() { this.calcularTotal(); },
    tipoDescuento() { this.calcularTotal(); },
    descuento() { this.calcularTotal(); },
    cantidad() { this.calcularTotal(); },
    mostrarDescuento() { this.calcularTotal(); },
    selectedPaquete() {
      this.precio = this.paqueteSeleccionado ? parseFloat(this.paqueteSeleccionado.precio) : 0;
      if (this.paqueteSeleccionado?.cantidad) this.cantidad = this.paqueteSeleccionado.cantidad;
      this.calcularTotal();
    },
    async dialog(open) {
      if (open) {
        try {
          await this.fetchCursos();
          if (!this.mediosContacto.length) {
            this.mediosContacto = await this.fetchMediosContacto();
          }
        } catch (error) {
          console.error('Error al cargar datos:', error);
        }
        return;
      }

      this.resetForm();
    },
  },

  methods: {
    ...mapActions('curso', ['fetchCursos']),
    ...mapActions('alumno', ['matricularAlumnoCurso', 'fetchMediosContacto']),

    updateDialog(v) { 
      this.$emit('update:dialog', v); 
    },
    
    close() { 
      this.$emit('update:dialog', false); 
    },

    etiquetaPaquete(p) {
      const detalle = p.cantidad
        ? `${p.cantidad} horas/clases`
        : (this.esCuantificable ? 'por hora/clase' : 'curso completo');
      return `${p.descripcion} — S/ ${Number(p.precio).toFixed(2)} (${detalle})`;
    },

    resetForm() {
      this.selectedCurso = null;
      this.selectedPaquete = null;
      this.error = null;
      this.subtotal = 0;
      this.descuentoMonto = 0;
      this.selectedMedioContacto = null;
      this.cantidad = 1;
      this.precio = 0;
      this.fecha = new Date().toISOString().slice(0, 10);
      this.total = 0;
      this.descuento = 0;
      this.comisionIzipay = 0;
      this.pagoVisa = false;
      this.tipoDescuento = 'porcentaje';
      this.mostrarDescuento = false;
      this.esCuantificable = false;
    },

    toggleDescuento() {
      this.mostrarDescuento = !this.mostrarDescuento;
      if (!this.mostrarDescuento) {
        this.descuento = 0;
        this.calcularTotal();
      }
    },

    // Vista previa: el backend recalcula el total con las mismas reglas (CalculoMatricula).
    calcularTotal() {
      const precio = +this.precio || 0;
      const cantidad = this.cantidadEditable ? Math.max(1, +this.cantidad || 1) : 1;
      const subtotal = this.redondear(precio * cantidad);

      let descuento = 0;
      if (this.mostrarDescuento && this.descuento > 0) {
        descuento = this.tipoDescuento === 'porcentaje'
          ? (subtotal * this.descuento) / 100
          : +this.descuento;
      }
      descuento = this.redondear(Math.min(Math.max(descuento, 0), subtotal));
      const neto = this.redondear(subtotal - descuento);

      if (this.pagoVisa) {
        const comision = neto * 0.0344;
        this.comisionIzipay = Math.ceil(Math.round((comision + comision * 0.18) * 1e6) / 1e6);
      } else {
        this.comisionIzipay = 0;
      }

      this.subtotal = subtotal;
      this.descuentoMonto = descuento;
      this.total = (neto + this.comisionIzipay).toFixed(2);
    },

    redondear(v) {
      return Math.round(v * 100) / 100;
    },

    async save() {
      if (!this.selectedCurso || !this.selectedPaquete) return;
      if (!this.alumnoId) return;
      if (this.esPrimeraMatricula && !this.selectedMedioContacto) return;

      const payload = {
        alumno_id: this.alumnoId,
        curso_id: this.selectedCurso,
        precio_curso_id: this.selectedPaquete,
        cantidad: this.cantidadEditable ? this.cantidad : null,
        descuento: this.descuentoMonto,
        visa: this.pagoVisa,
        fecha: this.fecha,
        medio_contacto_id: this.selectedMedioContacto,
      };

      this.guardando = true;
      this.error = null;
      try {
        await this.matricularAlumnoCurso(payload);
        this.resetForm();
        this.close();
        this.$emit('curso-matriculado');
      } catch (e) {
        const errores = e.response?.data?.errors;
        this.error = (errores && Object.values(errores)[0]?.[0])
          || e.response?.data?.message
          || 'No se pudo matricular.';
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>
